

export const dynamic = 'force-dynamic';

import { useEffect, useState } from "react";
import VendorReportsLayout, { useVendorPeriod, type Period } from "../VendorReportsLayout";
import { reportsApi, vendorsApi, mastersApi } from "@/lib/api";
import { Card, ExportButtons, ReportBody, TableScroll, money, pct, useCanSeeMoney, useReport, type ExportSpec } from "../../ReportKit";

interface VendorComparison {
  id: number;
  name: string;
  contact_person?: string | null;
  phone?: string | null;
  vendor_type?: string | null;
  services_count: number;
  events: number;
  cancelled_events: number;
  cancellation_rate: number;
  commission_type?: string | null;
  commission_value?: number;
  billed?: number;
  paid?: number;
  charged?: number;
  owed_now?: number;
  avg_cost_price?: number;
  avg_selling_price?: number;
}

interface ComparisonReport { can_view_money: boolean; vendors: VendorComparison[] }
interface Option { id: number; name: string; vendor_type_id?: number | null }

const asList = (res: any): Option[] => {
  const d = res?.data;
  return Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
};

/** Index of the best value, or -1 when there is nothing to pick between. */
const best = (values: number[], lowerIsBetter = false) => {
  if (values.length < 2 || values.every((v) => v === values[0])) return -1;
  const target = lowerIsBetter ? Math.min(...values) : Math.max(...values);
  return values.indexOf(target);
};

type Row = { label: string; value: (v: VendorComparison) => React.ReactNode; bestOf?: (v: VendorComparison) => number; lowerIsBetter?: boolean; money?: boolean };

const ROWS: { section: string; rows: Row[] }[] = [
  {
    section: "Contact",
    rows: [
      { label: "Category", value: (v) => v.vendor_type || "—" },
      { label: "Contact person", value: (v) => v.contact_person || "—" },
      { label: "Phone", value: (v) => v.phone || "—" },
    ],
  },
  {
    section: "Work in this period",
    rows: [
      { label: "Events served", value: (v) => v.events, bestOf: (v) => v.events },
      { label: "Cancelled events", value: (v) => v.cancelled_events },
      { label: "Cancellation rate", value: (v) => pct(v.cancellation_rate), bestOf: (v) => v.cancellation_rate, lowerIsBetter: true },
      { label: "Cost of their work", value: (v) => money(v.billed), money: true },
      { label: "Charged to clients", value: (v) => money(v.charged), money: true },
      { label: "Paid towards it", value: (v) => money(v.paid), money: true },
    ],
  },
  {
    section: "Account",
    rows: [
      { label: "Owed today (all events)", value: (v) => money(v.owed_now), money: true },
      { label: "Commission terms", value: (v) => (v.commission_type === "percentage" ? `${v.commission_value ?? 0}%` : money(v.commission_value)), money: true },
      { label: "Services offered", value: (v) => v.services_count },
      { label: "Avg cost price", value: (v) => money(v.avg_cost_price), money: true },
      { label: "Avg selling price", value: (v) => money(v.avg_selling_price), money: true },
    ],
  },
];

/** Excel / Print: one table per section above, one row per vendor; money only for roles that see it. */
function buildExport(d: ComparisonReport, showMoney: boolean, period: Period, category?: string): ExportSpec {
  const vendors = d.vendors;
  const terms = (v: VendorComparison) => (v.commission_type === "percentage" ? `${v.commission_value ?? 0}%` : money(v.commission_value));
  return {
    title: "Vendor Comparison",
    fileName: "vendor-comparison",
    period,
    summary: [{ label: "Vendors compared", value: vendors.length, type: "number" }],
    tables: [
      {
        title: "Contact",
        columns: [{ label: "Vendor" }, { label: "Category" }, { label: "Contact person" }, { label: "Phone" }],
        rows: vendors.map((v) => [v.name, v.vendor_type, v.contact_person, v.phone]),
      },
      {
        title: "Work in this period",
        columns: [
          { label: "Vendor" }, { label: "Events served", type: "number" }, { label: "Cancelled events", type: "number" },
          { label: "Cancellation rate", type: "percent" },
          ...(showMoney
            ? [
              { label: "Cost of their work", type: "money" as const },
              { label: "Charged to clients", type: "money" as const },
              { label: "Paid towards it", type: "money" as const },
            ]
            : []),
        ],
        rows: vendors.map((v) => [
          v.name, v.events, v.cancelled_events, v.cancellation_rate,
          ...(showMoney ? [v.billed, v.charged, v.paid] : []),
        ]),
      },
      {
        title: "Account",
        columns: [
          { label: "Vendor" },
          ...(showMoney ? [{ label: "Owed today (all events)", type: "money" as const }, { label: "Commission terms" }] : []),
          { label: "Services offered", type: "number" },
          ...(showMoney ? [{ label: "Avg cost price", type: "money" as const }, { label: "Avg selling price", type: "money" as const }] : []),
        ],
        rows: vendors.map((v) => [
          v.name,
          ...(showMoney ? [v.owed_now, terms(v)] : []),
          v.services_count,
          ...(showMoney ? [v.avg_cost_price, v.avg_selling_price] : []),
        ]),
      },
    ],
    notes: [
      ...(category ? [`All vendors in the category: ${category}.`] : []),
      ...(showMoney ? ["Cost of work = what their work costs us, never the price charged to the client."] : []),
    ],
  };
}

export default function VendorComparisonPage() {
  const [period, setPeriod] = useVendorPeriod();
  const canSeeMoney = useCanSeeMoney();
  const [selectedVendors, setSelectedVendors] = useState<number[]>([]);
  const [vendorType, setVendorType] = useState<number | undefined>(undefined);
  const [allVendors, setAllVendors] = useState<Option[]>([]);
  const [vendorTypes, setVendorTypes] = useState<Option[]>([]);
  const [compared, setCompared] = useState<{ ids: number[]; type?: number } | null>(null);

  useEffect(() => {
    vendorsApi.list().then((res: any) => setAllVendors(asList(res))).catch(() => setAllVendors([]));
    mastersApi.vendorTypes.list().then((res: any) => setVendorTypes(asList(res))).catch(() => setVendorTypes([]));
  }, []);

  // Re-runs when the period changes, so the comparison always matches the period shown
  const state = useReport<ComparisonReport>(
    async () => (await reportsApi.vendorComparison({
      from_date: period.from,
      to_date: period.to,
      vendor_ids: compared?.ids.length ? compared.ids : undefined,
      vendor_type_id: compared?.ids.length ? undefined : compared?.type,
    })).data,
    [period.from, period.to, JSON.stringify(compared)],
    compared !== null,
  );

  const canCompare = selectedVendors.length > 0 || !!vendorType;
  const toggleVendor = (id: number) =>
    setSelectedVendors((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const comparedCategory = compared && !compared.ids.length && compared.type
    ? vendorTypes.find((vt) => vt.id === compared.type)?.name
    : undefined;

  return (
    <VendorReportsLayout
      period={period}
      onPeriodChange={setPeriod}
      actions={
        <ExportButtons
          build={() => (state.data ? buildExport(state.data, canSeeMoney && state.data.can_view_money, period, comparedCategory) : null)}
          disabled={!compared || state.loading || !state.data?.vendors.length}
        />
      }
    >
      <Card title="Pick vendors to compare" subtitle="Choose vendors, or a category to compare all its vendors">
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          <select
            value={vendorType || ""}
            aria-label="Vendor category"
            onChange={(e) => setVendorType(e.target.value ? Number(e.target.value) : undefined)}
            className="h-10 flex-1 px-3 text-sm rounded-lg border border-stone-200 focus:border-amber-400 outline-none bg-white"
          >
            <option value="">All categories</option>
            {vendorTypes.map((vt) => <option key={vt.id} value={vt.id}>{vt.name}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setCompared({ ids: selectedVendors, type: vendorType })}
            disabled={!canCompare}
            className="h-10 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 disabled:opacity-50"
          >
            Compare {selectedVendors.length ? `${selectedVendors.length} vendor${selectedVendors.length === 1 ? "" : "s"}` : vendorType ? "category" : ""}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {allVendors
            .filter((v) => !vendorType || v.vendor_type_id === vendorType)
            .map((v) => {
              const on = selectedVendors.includes(v.id);
              return (
                <button key={v.id} type="button" onClick={() => toggleVendor(v.id)} aria-pressed={on}
                  className={`h-9 px-3 rounded-lg text-xs font-medium border ${on ? "bg-amber-50 border-amber-400 text-amber-700" : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"}`}>
                  {v.name}{on && " ✓"}
                </button>
              );
            })}
        </div>
      </Card>

      {compared && (
        <ReportBody state={state}>
          {(d) => {
            const showMoney = canSeeMoney && d.can_view_money;
            const vendors = d.vendors;
            if (vendors.length === 0) return <Card><p className="py-6 text-center text-sm text-stone-400">No vendors to compare.</p></Card>;
            return (
              <Card title="Comparison" subtitle="Cost of work = what their work costs us, never the price charged to the client">
                <TableScroll minWidth={200 + vendors.length * 150}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 bg-stone-50">
                        <th className="text-left px-3 py-3 text-xs font-semibold text-stone-500 uppercase sticky left-0 bg-stone-50">Metric</th>
                        {vendors.map((v) => <th key={v.id} className="text-center px-3 py-3 text-xs font-semibold text-stone-700">{v.name}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {ROWS.map((group) => {
                        const rows = group.rows.filter((r) => !r.money || showMoney);
                        if (rows.length === 0) return null;
                        return [
                          <tr key={group.section} className="bg-stone-50">
                            <td className="px-3 py-2.5 font-semibold text-stone-800" colSpan={vendors.length + 1}>{group.section}</td>
                          </tr>,
                          ...rows.map((row) => {
                            const bestIdx = row.bestOf ? best(vendors.map(row.bestOf), row.lowerIsBetter) : -1;
                            return (
                              <tr key={row.label}>
                                <td className="px-3 py-2.5 font-medium text-stone-700 whitespace-nowrap sticky left-0 bg-white">{row.label}</td>
                                {vendors.map((v, idx) => (
                                  <td key={v.id} className={`px-3 py-2.5 text-center tabular-nums ${idx === bestIdx ? "text-emerald-700 font-semibold" : "text-stone-700"}`}>
                                    {row.value(v)}{idx === bestIdx && " ★"}
                                  </td>
                                ))}
                              </tr>
                            );
                          }),
                        ];
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              </Card>
            );
          }}
        </ReportBody>
      )}
    </VendorReportsLayout>
  );
}
